import { genericMemo } from "@redotech/react-util/component";
import { useWindowEventListener } from "@redotech/react-util/event";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { Input } from "@redotech/ui/form";
import classNames from "classnames";
import {
  memo,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { alertOnFailure } from "./alert";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { Flex } from "./flex";
import { Form } from "./form";
import { ActionPortalContext, Actions } from "./page";
import * as pageFormCss from "./page-form.module.css";
import { Text } from "./text";
import { OptionalTooltip } from "./tooltip/optional-tooltip";

export type PageFormSave = (signal: AbortSignal) => Promise<void>;

export const PageForm = genericMemo(function PageForm<T>({
  children,
  initial,
  input,
  save,
  additionalActions,
  persistentActions,
  bottomSave = false,
  blockQueryChanges = true,
  saveButtonHoverContentSlot,
  onReset,
}: {
  children: ReactNode | ReactNode[];
  initial: T;
  input: Input<T>;
  save: PageFormSave;
  additionalActions?: ReactNode;
  /** Actions that always show in the header, regardless of unsaved changes. Rendered left of Save/Cancel. */
  persistentActions?: ReactNode;
  bottomSave?: boolean;
  // Whether or not to prompt the user to confirm changes on navigation attempt, when only query params change
  blockQueryChanges?: boolean;
  saveButtonHoverContentSlot?: ReactNode;
  onReset?(): void;
}) {
  const actionsPortal = useContext(ActionPortalContext);
  const id = useId();

  const [saveLoad, doSave] = useTriggerLoad((signal) =>
    alertOnFailure("Saving failed, please check your inputs")(async () => {
      await save(signal);
      return true as const;
    }),
  );

  // Useful for debugging why the form thinks it is unsaved.
  // Look for the trail of `input.changed` fields.
  useWindowEventListener(window, "dblclick", (event) => {
    if (event.altKey && (event.metaKey || event.ctrlKey)) {
      console.log("Current page form input", input);
      console.log("Current page form initial", initial);
    }
  });

  return (
    <>
      {!bottomSave && actionsPortal
        ? createPortal(
            <Flex align="center" gap="sm">
              {persistentActions}
              <Actions show={input.changed}>
                <PageFormActions
                  additionalActions={additionalActions}
                  form={id}
                  input={input}
                  saveButtonHoverContentSlot={saveButtonHoverContentSlot}
                  saveLoad={saveLoad}
                />
              </Actions>
            </Flex>,
            actionsPortal,
          )
        : null}
      <Form
        blockQueryChanges={blockQueryChanges}
        id={id}
        initial={initial}
        input={input}
        onReset={onReset}
        onSubmit={doSave}
      >
        {bottomSave ? (
          <Flex dir="column" flex={1} gap="none" position="relative">
            {children}
            <PageFormBottomActions
              hasErrors={!!input.allErrors.length}
              onDiscard={() => {
                const el = document.getElementById(id);
                if (el instanceof HTMLFormElement) {
                  el.reset();
                }
              }}
              onSave={doSave}
              pending={saveLoad.pending}
              saveButtonHoverContentSlot={saveButtonHoverContentSlot}
              show={input.changed || saveLoad.pending}
            />
          </Flex>
        ) : (
          children
        )}
      </Form>
    </>
  );
});

const PageFormActions = genericMemo(function PageFormActions<T>({
  form,
  input,
  saveLoad,
  additionalActions,
  saveButtonHoverContentSlot,
}: {
  form: string;
  input: Input<T>;
  saveLoad: LoadState<true | undefined>;
  additionalActions?: ReactNode;
  saveButtonHoverContentSlot?: ReactNode;
}) {
  const previousErrors = useRef<string[]>([]);

  useEffect(() => {
    const currentErrorMessages = input.allErrors.map((error) =>
      error.toString(),
    );
    const previousErrorMessages = previousErrors.current;

    // Only log if errors have actually changed
    const errorsChanged =
      currentErrorMessages.length !== previousErrorMessages.length ||
      currentErrorMessages.some(
        (msg, index) => msg !== previousErrorMessages[index],
      );

    if (errorsChanged) {
      console.log("Form validation errors changed:", {
        previous: previousErrorMessages,
        current: currentErrorMessages,
        hasErrors: currentErrorMessages.length > 0,
        errorCount: currentErrorMessages.length,
      });

      // Update the previous errors reference
      previousErrors.current = currentErrorMessages;
    }
  }, [input.allErrors]);
  return (
    <Flex gap="md">
      <OptionalTooltip
        meetsCondition={!!saveButtonHoverContentSlot}
        tooltipProps={{
          title: saveButtonHoverContentSlot,
          arrow: true,
          className: pageFormCss.saveButtonHoverSlotWrapper,
          componentsProps: { tooltip: { sx: { maxWidth: "none" } } },
        }}
      >
        <RedoButton
          disabled={!!input.allErrors.length || !input.changed}
          form={form}
          hierarchy="primary"
          pending={saveLoad.pending}
          size="sm"
          text="Save"
          type="submit"
        />
      </OptionalTooltip>
      <RedoButton
        disabled={saveLoad.pending || !input.changed}
        form={form}
        hierarchy="secondary"
        size="sm"
        text="Cancel"
        type="reset"
      />
      {additionalActions}
    </Flex>
  );
});

/**
 * Floating bottom bar that appears when there are unsaved changes.
 * Renders a spacer (to push content up) and a fixed-position dark pill bar
 * with "Discard" and "Save changes" buttons.
 *
 * Place this component at the bottom of the scrollable content container so
 * the spacer and fixed-bar width track the content area (not the full viewport).
 */
export const PageFormBottomActions = memo(function PageFormBottomActions({
  show,
  pending,
  hasErrors,
  onSave,
  onDiscard,
  saveButtonHoverContentSlot,
}: {
  show: boolean;
  pending: boolean;
  hasErrors?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  saveButtonHoverContentSlot?: ReactNode;
}) {
  const spacerRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const el = spacerRef.current;
    if (!el) {
      return;
    }
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, width: r.width });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const style = useMemo(
    () => ({
      width: rect.width ? `${rect.width}px` : "100%",
      left: `${rect.left}px`,
      right: "auto",
    }),
    [rect],
  );
  return (
    <>
      <div
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        className={classNames(pageFormCss.spacer, { [pageFormCss.show]: show })}
        ref={spacerRef}
      />
      <Flex
        className={classNames(pageFormCss.notificationContainer, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [pageFormCss.show]: show,
        })}
        justify="center"
        style={style}
      >
        <Flex
          align="center"
          aria-hidden={!show}
          className={pageFormCss.notificationBar}
          dir="row"
          flex={1}
          justify="space-between"
        >
          <Text fontSize="sm" fontWeight="regular" textColor="white">
            You have unsaved changes.
          </Text>
          <Flex gap="lg">
            <RedoButton
              className={pageFormCss.discard}
              disabled={pending}
              hierarchy="link"
              onClick={onDiscard}
              size="sm"
              text="Discard"
              type="button"
            />
            <OptionalTooltip
              meetsCondition={!!saveButtonHoverContentSlot}
              tooltipProps={{
                title: saveButtonHoverContentSlot,
                arrow: true,
                className: pageFormCss.saveButtonHoverSlotWrapper,
                componentsProps: { tooltip: { sx: { maxWidth: "none" } } },
              }}
            >
              <RedoButton
                disabled={!!hasErrors}
                hierarchy="primary"
                onClick={onSave}
                pending={pending}
                size="sm"
                text="Save changes"
                theme="success"
                type="button"
              />
            </OptionalTooltip>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
});
